// import { EventSourcePolyfill } from 'libs/eventsource'

import { getCapabilitiesForMethodCalls, knownCapabilities } from './capabilities'
import { expandURITemplate, getErrorFromInvocation, getResultsForMethodCalls } from './helpers'
import { buildRequestsFromDrafts } from './request-drafts'

import type {
  WithRevValues,
  WithoutRefValues,
  DraftsProxy,
  InvocationDraft,
} from './request-drafts'

import type {
  GetArgs,
  GetResponseData,
  LocalInvocation,
  Meta,
  Methods,
  ProxyAPI,
  RequestOptions,
} from './types/contracts'

import type * as JMAP from './types/jmap'
import type * as JMAPMail from './types/jmap-mail'
import EventSourcePolyfill from 'libs/eventsource'

// console.log('eventsource - ', EventSourcePolyfill)

const skipOnce401 = true

export type ClientConfig = {
  /**
   * The tokenType used to authenticate all requests. (bearer, basic, jwt ...)
   */
  tokenType: string

  /**
   * The bearer token used to authenticate all requests
   */
  token: string

  /**
   * The URL of the JMAP session resources
   */
  sessionUrl: string

  /**
   * The base URL of the JMAP api
   */
  baseUrl?: string

  /**
   * The custom fetch function to use for requests
   */
  customFetch?: (url: string | URL | globalThis.Request, options?: RequestInit) => Promise<Response>

  /**
   * A map of custom entities and their required capability identifiers
   *
   * @example
   * ```
   * const client = createClient({
   *   customCapabilities: {
   *     "Sandwich": "urn:bigco:params:jmap:sandwich",
   *     "TextMessage": "foo:bar:jmap:sms",
   *     "Spaceship": "myspaceship-jmap-urn",
   *   },
   * });
   * ```
   */
  customCapabilities?: Record<string, string>
}

export class JamClient<Config extends ClientConfig = ClientConfig> {
  /**
   * Headers to send with every request
   */
  authHeader: string

  /**
   * The custom fetch function to use for requests
   */
  fetchFunc: (url: string | URL | globalThis.Request, options?: RequestInit) => Promise<Response>

  /**
   * All available capabilities (known and custom)
   */
  capabilities: Map<string, string>

  /**
   * An immediately fetched session promise
   */
  session: Promise<JMAP.Session>

  constructor(config: Config) {
    this.setAuthInfo(config.tokenType, config.token)

    this.fetchFunc = (url: string | URL | globalThis.Request, options?: RequestInit) => {
      if (config.customFetch != null) {
        return config.customFetch(url, options)
      }
      return fetch(url, options)
    }

    this.capabilities = new Map<string, string>([
      ...Object.entries(config.customCapabilities ?? {}),
      ...Object.entries(knownCapabilities),
    ])

    this.session = JamClient.loadSession(
      config.sessionUrl,
      this.fetchFunc,
      this.authHeader,
      config.baseUrl,
    )
  }

  /**
   * Retrieve fresh session data
   */
  static async loadSession(
    sessionUrl: string,
    fetchFunc: (url: string | URL | globalThis.Request, options?: RequestInit) => Promise<Response>,
    authHeader: string,
    baseUrl?: string,
  ) {
    return await fetchFunc(sessionUrl, {
      headers: {
        Authorization: authHeader,
        Accept: 'application/json',
      },
      cache: 'no-cache',
    }).then(async (res) => {
      const sessionInfo = await res.json()
      console.log('- sessionInfo : ', sessionInfo)
      if (baseUrl != null) {
        Object.entries(sessionInfo).forEach(([key, value]) => {
          if (key.endsWith('Url') === true) {
            const val = String(value)
            if (val.indexOf('https:') !== 0 && val.indexOf('http://') !== 0) {
              sessionInfo[key] = `${baseUrl}${val}`
            }
          }
        })
      }
      console.log('- sessionInfo2 : ', sessionInfo)
      return sessionInfo
    })
  }

  setAuthInfo(tokenType: string, token: string) {
    this.authHeader = `${tokenType} ${token}`
  }

  async requestFunc(
    url: string | URL,
    body: BodyInit | null,
    fetchInit: RequestInit,
    accept: string | null = null,
    contentType: string | null = null,
  ) {
    const headers = {
      Authorization: this.authHeader,
    }
    if (accept != null) {
      headers['Accept'] = accept
    }
    if (contentType != null) {
      headers['Content-Type'] = contentType
    }

    const options = {
      method: 'POST',
      headers,
      ...fetchInit,
    }

    if (body != null) {
      options.body = body
    }

    return await this.fetchFunc(url, options)
  }

  /**
   * Send a JMAP request containing a single method call
   */
  async request<
    Method extends Methods,
    Args extends GetArgs<Method, Args>,
    Data extends GetResponseData<Method, Args>,
  >(
    [method, args]: LocalInvocation<Method, Args>,
    options?: RequestOptions,
  ): Promise<[Data, Meta]> {
    const { using = [], fetchInit, createdIds: createdIdsInput } = options ?? {}

    // Assemble method call
    const invocation: JMAP.Invocation<Args> = [method, args, 'r1']

    const capabilities = getCapabilitiesForMethodCalls({
      methodNames: [method],
      availableCapabilities: this.capabilities,
    })

    // Build request
    const body: JMAP.Request<[JMAP.Invocation<Args>]> = {
      using: [...Array.from(capabilities), ...using],
      methodCalls: [invocation],
      createdIds: createdIdsInput,
    }

    // Ensure session is loaded (if not already)
    const { apiUrl } = await this.session

    const jsonBody = JSON.stringify(body)
    let response = await this.requestFunc(
      apiUrl,
      jsonBody,
      fetchInit,
      'application/json',
      'application/json',
    )

    const processError = async (res) => {
      let error: string | JMAP.ProblemDetails

      if (res.headers.get('Content-Type')?.includes('json')) {
        error = (await res.json()) as JMAP.ProblemDetails
      } else {
        error = await res.text()
      }

      throw error
    }

    if (skipOnce401 === true) {
      // 두번 연속 401 에러 발생시에만 실제 에러로 처리.
      if (response.status === 401) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        response = await this.requestFunc(
          apiUrl,
          jsonBody,
          fetchInit,
          'application/json',
          'application/json',
        )
      }
    }

    // Handle 4xx-5xx errors
    if (!response.ok) {
      await processError(response)
    }

    // Handle success
    const {
      methodResponses: [methodResponse],
      sessionState,
      createdIds,
    } = (await response.json()) as JMAP.Response<[JMAP.Invocation<Data>]>

    const error = getErrorFromInvocation(methodResponse)
    if (error) {
      throw error
    }

    return [
      methodResponse[1],
      {
        sessionState,
        createdIds,
        response,
      },
    ]
  }

  async requestMany<
    DraftsFn extends (b: DraftsProxy) => { [id: string]: InvocationDraft },
    Returning extends ReturnType<DraftsFn>,
  >(
    draftsFn: DraftsFn,
    options: RequestOptions = {},
  ): Promise<
    [
      {
        [MethodId in keyof Returning]: Returning[MethodId] extends InvocationDraft<
          infer Inv extends [Methods, WithRevValues<Record<string, any>>]
        >
          ? GetResponseData<Inv[0], WithoutRefValues<Inv[1]>>
          : never
      },
      Meta,
    ]
  > {
    // Extract options
    const { using = [], fetchInit, createdIds: createdIdsInput } = options

    const { methodCalls, methodNames } = buildRequestsFromDrafts(draftsFn)

    const capabilities = getCapabilitiesForMethodCalls({
      methodNames,
      availableCapabilities: this.capabilities,
    })

    console.log(' --- capabilities --- ', capabilities)

    // Build request
    const body: JMAP.Request = {
      using: [...Array.from(capabilities), ...using],
      methodCalls,
      createdIds: createdIdsInput,
    }

    // Ensure session is loaded (if not already)
    const { apiUrl } = await this.session

    const jsonBody = JSON.stringify(body)
    let response = await this.requestFunc(
      apiUrl,
      jsonBody,
      fetchInit,
      'application/json',
      'application/json',
    )

    const processError = async (res) => {
      let error: string | JMAP.ProblemDetails

      if (res.headers.get('Content-Type')?.includes('json')) {
        error = (await res.json()) as JMAP.ProblemDetails
      } else {
        error = await res.text()
      }

      throw error
    }

    if (skipOnce401 === true) {
      // 두번 연속 401 에러 발생시에만 실제 에러로 처리.
      if (response.status === 401) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        response = await this.requestFunc(
          apiUrl,
          jsonBody,
          fetchInit,
          'application/json',
          'application/json',
        )
      }
    }

    // Handle 4xx-5xx errors
    if (!response.ok) {
      await processError(response)
    }

    // Handle success
    const { methodResponses, sessionState, createdIds } = (await response.json()) as JMAP.Response

    const meta: Meta = {
      sessionState,
      createdIds,
      response,
    }

    const errors = methodResponses
      .map(getErrorFromInvocation)
      .filter((e): e is NonNullable<typeof e> => e !== null)

    if (errors.length > 0) {
      throw errors
    }

    return [
      getResultsForMethodCalls(methodResponses, { returnErrors: false }, methodCalls),
      meta,
    ] as [
      {
        [MethodId in keyof Returning]: Returning[MethodId] extends InvocationDraft<
          infer Inv extends [Methods, WithRevValues<Record<string, any>>]
        >
          ? GetResponseData<Inv[0], WithoutRefValues<Inv[1]>>
          : never
      },
      Meta,
    ]
  }

  /**
   * Get the ID of the primary mail account for the current session
   */
  async getPrimaryAccount() {
    return (await this.session).primaryAccounts?.['urn:ietf:params:jmap:mail']
  }

  /**
   * Upload a blob
   */
  async uploadBlob(
    accountId: JMAP.BlobUploadParams['accountId'],
    body: BodyInit,
    fetchInit: RequestInit = {},
  ) {
    const { uploadUrl } = await this.session

    const params: JMAP.BlobUploadParams = {
      accountId,
    }

    const url = expandURITemplate(uploadUrl, params)

    try {
      let response = await this.requestFunc(url, body, fetchInit, 'application/json')

      if (skipOnce401 === true) {
        // 두번 연속 401 에러 발생시에만 실제 에러로 처리.
        if (response.status === 401) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          response = await this.requestFunc(url, body, fetchInit, 'application/json')
        }
      }

      if (!response.ok) {
        if (response.headers.get('Content-Type')?.includes('json')) {
          throw (await response.json()) as JMAP.ProblemDetails
        } else {
          throw await response.text()
        }
      }

      return (await response.json()) as JMAP.BlobUploadResponse
    } catch (cause) {
      throw new Error(`Failed to upload blob - ${cause}`)
    }
  }

  /**
   * Download a blob
   */
  async downloadBlob(
    options: {
      accountId: JMAP.BlobDownloadParams['accountId']
      blobId: JMAP.BlobDownloadParams['blobId']
      mimeType: JMAP.BlobDownloadParams['type']
      fileName: JMAP.BlobDownloadParams['name']
    },
    fetchInit: RequestInit = {},
  ) {
    const { downloadUrl } = await this.session

    const params: JMAP.BlobDownloadParams = {
      accountId: options.accountId,
      blobId: options.blobId,
      type: options.mimeType,
      name: options.fileName,
    }

    const url = expandURITemplate(downloadUrl, params)

    try {
      let response = await this.requestFunc(url, null, fetchInit)

      if (skipOnce401 === true) {
        // 두번 연속 401 에러 발생시에만 실제 에러로 처리.
        if (response.status === 401) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          response = await this.requestFunc(url, null, fetchInit)
        }
      }

      if (!response.ok) {
        if (response.headers.get('Content-Type')?.includes('json')) {
          throw (await response.json()) as JMAP.ProblemDetails
        } else {
          throw await response.text()
        }
      }

      return response
    } catch (cause) {
      throw new Error(`Failed to download blob - ${cause}`)
    }
  }

  /**
   * Initiate an event source to subscribe to server-sent events
   */
  async connectEventSource(
    options: {
      types: '*' | Array<JMAPMail.Entity>
      ping: number
      closeafter?: JMAP.EventSourceArguments['closeafter']
    },
    opts: { [key: string]: any },
  ) {
    const params: JMAP.EventSourceArguments = {
      types: options.types === '*' ? '*' : options.types.join(','),
      closeafter: options.closeafter ?? 'no',
      ping: `${options.ping}`,
    }

    const { eventSourceUrl } = await this.session

    const url = expandURITemplate(eventSourceUrl, params)

    const urlString = url.toString()

    let evtOpts = {
      headers: {},
      withCredentials: false,
    }

    if (this.authHeader != null) {
      evtOpts.headers = {
        Authorization: this.authHeader,
      }
    }

    if (opts != null) {
      const tmpHeader = {
        ...evtOpts.headers,
        ...opts.headers,
        // TE: 'trailers',
        // 'Transfer-Encoding': 'trailers',
        // 'Content-Encoding': '',
        // 'Accept-Encoding': 'gzip;q=0,deflate;q=0',
      }

      evtOpts = {
        ...evtOpts,
        ...opts,
        headers: tmpHeader,
      }
    }

    return new EventSourcePolyfill(urlString, evtOpts)
    // return new EventSource(urlString, evtOpts)
  }

  /**
   * A fluent API using {entity}.{operation} syntax
   *
   * @example
   * ```ts
   * const [emails] = await jam.api.Email.get({
   *   accountId,
   *   ids,
   *   properties,
   * });
   *
   * const [mailboxes] = await jam.api.Mailbox.query({
   *   accountId,
   *   filter: { name: "Inbox" },
   * });
   * ```
   */
  get api() {
    return new Proxy<ProxyAPI>({} as ProxyAPI, {
      get: (_, entity: string) =>
        new Proxy(
          {},
          {
            get: (__, operation: string) => {
              return async (args: any, options?: RequestOptions) => {
                const method = `${entity}/${operation}` as Methods

                return this.request([method, args], options)
              }
            },
          },
        ),
    })
  }

  static isProblemDetails(value: unknown): value is JMAP.ProblemDetails {
    return typeof value === 'object' && value !== null && 'type' in value
  }
}
